import type { CycleType } from "./hooks/useCycles";

/**
 * Cycle types whose treatment includes monitoring the patient's own response —
 * follicle sizes, estradiol, endometrial lining, day by day.
 *
 * Membership is listed rather than derived by exclusion so a cycle type added
 * to the API later has to opt in deliberately. Inheriting a clinical surface by
 * default is how a section ends up on a cycle it does not belong to.
 *
 * The reasoning per type:
 *   - ivf / icsi / egg_freezing / embryo_freezing — ovarian stimulation, the
 *     original case: follicles are scanned until the trigger.
 *   - oi — ovulation induction is a stimulated cycle, scanned the same way.
 *   - iui — the follicles are tracked to time the insemination.
 *   - fet — no stimulation, but the lining and estradiol are followed to time
 *     the transfer, and those are two of this section's three series.
 *
 * Absent, deliberately: sperm_freezing. Nothing about the patient's cycle is
 * monitored there, so the section would only ever show its empty state.
 */
const MONITORED_CYCLE_TYPES = new Set<string>([
  "ivf",
  "icsi",
  "egg_freezing",
  "embryo_freezing",
  "oi",
  "iui",
  "fet",
]);

/**
 * Whether a cycle of this type is monitored, and so whether the monitoring
 * section belongs on its detail screen.
 *
 * Several raw values can describe one cycle (the API's `cycleType` and whatever
 * the clinic wrote), so every candidate is considered and any match counts.
 * An unrecognised or missing type is treated as monitored: a cycle whose type
 * was never recorded is more likely to be a stimulation cycle than a sperm
 * freeze, and hiding a section the patient has data in is the worse mistake.
 */
export function isMonitoredCycleType(
  ...values: (CycleType | string | null | undefined)[]
): boolean {
  const known = values.filter(
    (value): value is string => typeof value === "string" && value.length > 0,
  );
  if (known.length === 0) return true;
  const normalized = known.map((value) =>
    value
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_"),
  );
  // An unrecognised label says nothing either way, so it does not veto.
  const recognised = normalized.filter(
    (value) => value === "sperm_freezing" || MONITORED_CYCLE_TYPES.has(value),
  );
  if (recognised.length === 0) return true;
  return recognised.some((value) => MONITORED_CYCLE_TYPES.has(value));
}

/**
 * Whether this cycle's monitoring is about preparing the endometrium rather
 * than growing follicles.
 *
 * A frozen transfer has no stimulation: what is followed is the lining and the
 * hormones that time the transfer. Calling that surface "stimulation
 * monitoring" describes something the cycle does not do.
 */
export function isLiningPreparationCycleType(
  value: string | null | undefined,
): boolean {
  if (typeof value !== "string") return false;
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_") === "fet"
  );
}
